import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import PersonIcon from "@mui/icons-material/Person";
import { useState } from "react";
import { format } from "date-fns";

export default function ReservationForm({ roomId, onNext }) {
  const [guests, setGuests] = useState([
    { firstName: "", lastName: "", email: "", phone: "" },
  ]);

  // Default dates
  const today = new Date();
  const sevenDaysLater = new Date();
  sevenDaysLater.setDate(today.getDate() + 7);

  const formatInputDate = (date) => format(date, "yyyy-MM-dd");

  const [checkin, setCheckin] = useState(formatInputDate(today));
  const [checkout, setCheckout] = useState(formatInputDate(sevenDaysLater));
  const [checkoutError, setCheckoutError] = useState(false);

  // Validate checkout > checkin
  const handleDateChange = (type, value) => {
    if (type === "checkin") {
      setCheckin(value);
      const newCheckin = new Date(value);
      const currentCheckout = new Date(checkout);
      if (currentCheckout <= newCheckin) {
        // auto-set checkout to next day
        const nextDay = new Date(newCheckin);
        nextDay.setDate(newCheckin.getDate() + 1);
        setCheckout(formatInputDate(nextDay));
        setCheckoutError(false);
      }
    } else if (type === "checkout") {
      setCheckout(value);
      const newCheckout = new Date(value);
      const currentCheckin = new Date(checkin);
      if (newCheckout <= currentCheckin) {
        setCheckoutError(true);
      } else {
        setCheckoutError(false);
      }
    }
  };

  // Calculate selected days
  const daysSelected =
    Math.ceil(
      (new Date(checkout) - new Date(checkin)) / (1000 * 60 * 60 * 24)
    ) || 0;

  const addGuest = () => {
    setGuests([
      ...guests,
      { firstName: "", lastName: "", email: "", phone: "" },
    ]);
  };

  const removeGuest = (index) => {
    setGuests((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <Box>
      {/* Header Row – Left: Room Info, Right: Days Selected */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h5" fontWeight={600}>
          Room {roomId} — Reservation Details
        </Typography>
        <Typography
          variant="subtitle1"
          fontWeight={500}
          color={checkoutError ? "error.main" : "green"}
        >
          {checkoutError
            ? "Checkout must be after Checkin"
            : `Selected for ${daysSelected} ${
                daysSelected === 1 ? "day" : "days"
              }`}
        </Typography>
      </Box>

      {/* Compact Reservation Container */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          backgroundColor: "#f8fafc",
          border: "1px solid #e0e6ed",
        }}
      >
        <Grid container spacing={2}>
          {/* Row 1 – Arrival / Departure / Rent */}
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Checkin"
              type="date"
              size="small"
              value={checkin}
              onChange={(e) => handleDateChange("checkin", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Expected Checkout"
              type="date"
              size="small"
              value={checkout}
              onChange={(e) => handleDateChange("checkout", e.target.value)}
              error={checkoutError}
              helperText={
                checkoutError ? "Checkout must be after Checkin" : ""
              }
              InputProps={{
                inputProps: { min: checkin }, // prevents choosing earlier checkout
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Rent"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Row 2 – Deposit / Adults / Children / Pets */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Deposit"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AttachMoneyIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Adults"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Children"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Pets"
              type="number"
              size="small"
              inputProps={{ min: 0 }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Divider – Guest Section */}
      <Divider sx={{ my: 1 }} />

      {/* Guest Section Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={1}
      >
        <Typography variant="h5" fontWeight={600}>
          Guest Details
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={addGuest}
          sx={{
            backgroundColor: "#4f9deb",
            textTransform: "none",
            "&:hover": { backgroundColor: "#3b8edb" },
          }}
        >
          Add Guest
        </Button>
      </Box>

      {/* Dynamic Guest Forms */}
      {guests.map((g, i) => (
        <Paper
          key={i}
          elevation={0}
          sx={{
            backgroundColor: "#f0f6fc",
            border: "1px solid #d3e2f3",
            borderRadius: 2,
            p: 3,
            mb: 2,
            position: "relative",
          }}
        >
          {/* X Remove Icon (Top-Right) */}
          {guests.length > 1 && (
            <IconButton
              onClick={() => removeGuest(i)}
              size="small"
              color="error"
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "#fff",
                boxShadow: 1,
                "&:hover": { backgroundColor: "#f8d7da" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Action Button */}
      <Box textAlign="right" mt={4}>
        <Button
          variant="contained"
          size="large"
          onClick={() => onNext({ guests })}
          disabled={checkoutError}
          sx={{
            backgroundColor: "#4f9deb",
            "&:hover": { backgroundColor: "#3b8edb" },
            textTransform: "none",
          }}
        >
          Continue → Documents
        </Button>
      </Box>
    </Box>
  );
}
